/**
 * LanguageModal Component
 * Modal for selecting app language
 */

import { useState } from 'react';
import { api } from '../services/api';
import { useUserContext } from '../contexts';

interface LanguageModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentLanguage: string;
}

interface Language {
  code: 'en' | 'ru' | 'tr';
  name: string;
  nativeName: string;
  flag: string;
}

const languages: Language[] = [
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇺🇸' },
  { code: 'ru', name: 'Russian', nativeName: 'Русский', flag: '🇷🇺' },
  { code: 'tr', name: 'Turkish', nativeName: 'Türkçe', flag: '🇹🇷' },
];

export function LanguageModal({ isOpen, onClose, currentLanguage }: LanguageModalProps) {
  const { refreshUser } = useUserContext();
  const [isLoading, setIsLoading] = useState(false);

  const handleLanguageSelect = async (languageCode: string) => {
    if (languageCode === currentLanguage) {
      onClose();
      return;
    }

    setIsLoading(true);
    try {
      await api.updateSettings({ language: languageCode as 'ru' | 'en' | 'tr' });
      await refreshUser();
      onClose();
    } catch (error) {
      console.error('Failed to update language:', error);
      alert('Failed to update language. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px',
    }}>
      <div style={{
        background: 'linear-gradient(180deg, #1a2332 0%, #0a0e1a 100%)',
        borderRadius: '20px',
        padding: '24px',
        maxWidth: '400px',
        width: '100%',
        border: '1px solid rgba(59, 130, 246, 0.3)',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
        }}>
          <h2 style={{ 
            margin: 0, 
            fontSize: '24px', 
            fontWeight: 700,
            color: '#fff',
          }}>
            Select Language
          </h2>
          <button
            onClick={onClose}
            disabled={isLoading}
            style={{
              background: 'none',
              border: 'none',
              color: '#fff',
              fontSize: '24px',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              padding: 0,
              opacity: isLoading ? 0.5 : 1,
            }}
          >
            ×
          </button>
        </div>

        {/* Language Options */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
        }}>
          {languages.map((language) => (
            <button
              key={language.code}
              onClick={() => handleLanguageSelect(language.code)}
              disabled={isLoading}
              style={{
                width: '100%',
                padding: '16px',
                background: currentLanguage === language.code 
                  ? 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)'
                  : 'rgba(10, 22, 40, 0.6)',
                border: currentLanguage === language.code
                  ? '2px solid #3b82f6'
                  : '1px solid rgba(59, 130, 246, 0.2)',
                borderRadius: '12px',
                color: '#fff',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                fontSize: '16px',
                fontWeight: currentLanguage === language.code ? 600 : 400,
                transition: 'all 0.2s',
                opacity: isLoading ? 0.7 : 1,
              }}
              onMouseEnter={(e) => {
                if (!isLoading && currentLanguage !== language.code) {
                  e.currentTarget.style.background = 'rgba(10, 22, 40, 0.8)';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isLoading && currentLanguage !== language.code) {
                  e.currentTarget.style.background = 'rgba(10, 22, 40, 0.6)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }
              }}
            >
              {/* Flag */}
              <div style={{
                fontSize: '32px',
                width: '48px',
                height: '48px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '8px',
                background: 'rgba(255, 255, 255, 0.1)',
              }}>
                {language.flag}
              </div>

              {/* Language Info */}
              <div style={{
                flex: 1,
                textAlign: 'left',
              }}>
                <div style={{
                  fontSize: '18px',
                  fontWeight: 600,
                  marginBottom: '2px',
                }}>
                  {language.nativeName}
                </div>
                <div style={{
                  fontSize: '14px',
                  color: 'rgba(255, 255, 255, 0.7)',
                }}>
                  {language.name}
                </div>
              </div>

              {/* Check Mark */}
              {currentLanguage === language.code && (
                <div style={{
                  fontSize: '20px',
                  color: '#10b981',
                  fontWeight: 'bold',
                }}>
                  ✓
                </div>
              )}
            </button>
          ))}
        </div>

        {/* Loading State */}
        {isLoading && (
          <div style={{
            marginTop: '16px',
            textAlign: 'center',
            color: 'rgba(255, 255, 255, 0.7)',
            fontSize: '14px',
          }}>
            Updating language...
          </div>
        )}
      </div>
    </div>
  );
}